import { useState, useEffect, lazy, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import BlogSubNav from './BlogSubNav';
import Footer from './Footer';
// L'assistant n'est utile qu'apres lecture : il n'a pas a etre dans le bundle
// initial (28 Ko de source, aucun appel reseau au montage).
const ChatBot = lazy(() => import('./ChatBot'));
import AdBlock from './AdBlock';
import PWAInstallPrompt from './PWAInstallPrompt';
import { fetchSiteSettings } from '../hooks/useSiteData';

const AdminToolbar = lazy(() => import('./AdminToolbar'));
const VisualEditor = lazy(() => import('./VisualEditor'));

const Layout = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const isWorkspace = /^\/(editor|dashboard|kiosque\/espace-editeur)(\/|$)/.test(location.pathname);
  const isPreview = location.pathname === '/preview';
  const [editMode, setEditMode] = useState(false);

  // Filet anti-blocage mobile : si un overlay (menu, modal, lightbox) a laissé le
  // <body> verrouillé (overflow:hidden) après sa fermeture, la navigation suivante
  // rétablit le défilement — évite l'« écran figé » signalé sur smartphone.
  useEffect(() => {
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }
  }, [location.pathname]);

  // Apply typography settings from site-settings
  useEffect(() => {
    const applyTypography = async () => {
      try {
        const data = await fetchSiteSettings();
        const typo = data?.typography;
        if (!typo) return;
        const root = document.documentElement;
        if (typo.heading_font) {
          root.style.setProperty('--font-serif', `'${typo.heading_font}', serif`);
        }
        if (typo.body_font) {
          root.style.setProperty('--font-sans', `'${typo.body_font}', sans-serif`);
        }
        if (typo.base_size) {
          root.style.fontSize = `${typo.base_size}px`;
        }
        // Preload Google Fonts
        // Playfair Display et DM Sans sont déjà servies par index.html : les
        // réinjecter provoquait un second échange de police après le premier
        // rendu, donc un décalage visible.
        const PRELOADED = ['Playfair Display', 'DM Sans'];
        const fonts = [typo.heading_font, typo.body_font]
          .filter(Boolean)
          .filter(f => !PRELOADED.includes(f));
        const families = fonts.map(f => f.replace(/ /g, '+')).join('&family=');
        if (families) {
          const linkId = 'dynamic-fonts';
          let link = document.getElementById(linkId);
          if (!link) {
            link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
          }
          link.href = `https://fonts.googleapis.com/css2?family=${families}:wght@400;500;600;700&display=swap`;
        }
      } catch {
        // Settings not available, keep defaults
      }
    };
    applyTypography();
  }, []);

  const handleSave = async () => {
    window.dispatchEvent(new CustomEvent('admin-save'));
  };

  return (
    <div className={`min-h-screen flex flex-col bg-background ${isAdmin && !isPreview ? 'has-admin-toolbar' : ''}`}>
      {isAdmin && !isPreview && (
        <Suspense fallback={<div className="h-10 sm:h-12 bg-gray-900" />}>
          <AdminToolbar
            editMode={editMode}
            setEditMode={setEditMode}
            onSave={handleSave}
          />
        </Suspense>
      )}
      <div className={isAdmin && !isPreview ? 'pt-10 sm:pt-12' : ''}>
        <Header />
        <BlogSubNav />
        <main className="flex-1">
          <Outlet />
        </main>
        <AdBlock placement="footer" className="container mx-auto px-4 py-4" />
        <Footer />
      </div>
      {/* Les flottants du site (assistant, bannière PWA) partagent le bas de
          l'écran avec la barre d'actions de l'éditeur sur mobile : on les retire
          des espaces de travail pour que « Publier » reste cliquable. */}
      {!isWorkspace && <Suspense fallback={null}><ChatBot /></Suspense>}
      {!isWorkspace && <PWAInstallPrompt />}
      {isAdmin && !isPreview && editMode && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/20 z-40" />}>
          <VisualEditor editMode={editMode} />
        </Suspense>
      )}
    </div>
  );
};

export default Layout;
